import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, DashboardLayout } from '../components';
import { Breadcrumb } from '../components/Breadcrumb';
import { personApi } from '../services/api';
import type { InvitationDetails } from '../types';

export function PersonInvitationStatus() {
  const { t } = useTranslation();

  const [invitations, setInvitations] = useState<InvitationDetails[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [registrationUrls, setRegistrationUrls] = useState<Record<string, string>>({});
  const [actionError, setActionError] = useState('');

  useEffect(() => {
    loadInvitations();
  }, []);




  const loadInvitations = async () => {
    try {
      const data = await personApi.getInvitations();
      setInvitations(data);
    } catch (error) {
      console.error('Failed to load invitations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async (invitation: InvitationDetails) => {
    setResendingId(invitation.id);
    setActionError('');
    try {
      const updated = await personApi.resendInvitation(invitation.id);
      setInvitations((current) =>
        current.map((item) => (item.id === updated.id ? { ...item, ...updated } : item))
      );
      if (updated.registrationUrl) {
        setRegistrationUrls((current) => ({ ...current, [updated.id]: updated.registrationUrl! }));
      }
    } catch {
      setActionError(t('personInvitations.resendError'));
    } finally {
      setResendingId(null);
    }
  };

  const handleCopyLink = (id: string) => {
    const url = registrationUrls[id];
    if (!url) return;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    });
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-warning';
      case 'sent': return 'bg-info';
      case 'accepted': return 'bg-success';
      case 'rejected': return 'bg-danger';
      case 'revoked': return 'bg-secondary';
      default: return 'bg-secondary';
    }
  };

  return (
    <DashboardLayout>
            <div className="page-header mb-4">
              <div className="page-header-left d-flex align-items-center">
                <div className="page-header-title">
                  <h5 className="fw-bold mb-0">{t('personInvitations.title')}</h5>
                </div>
                <Breadcrumb items={[
                  { label: t('nav.home'), path: '/home' },
                  { label: t('personInvitations.title') }
                ]} />
              </div>
              <div className="page-header-right ms-auto d-none d-sm-block">
                <p className="fw-bold text-muted mb-0">
                  {t('personInvitations.description')}
                </p>
              </div>
            </div>

            <Card>
              {actionError && <div className="alert alert-danger">{actionError}</div>}
              {isLoading ? (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">{t('common.loading')}</span>
                  </div>
                </div>
              ) : invitations.length === 0 ? (
                <div className="text-center py-5 text-muted">
                  <i className="feather-inbox fs-1 mb-3 d-block"></i>
                  <p>{t('personInvitations.noInvitations')}</p>
                </div>
              ) : (
                <>
                  <div className="d-none d-md-block">
                    <div className="table-responsive">
                      <table className="table table-hover">
                        <thead>
                          <tr>
                            <th>{t('personInvitations.columns.companyName')}</th>
                            <th>{t('personInvitations.columns.contactEmail')}</th>
                            <th>{t('personInvitations.columns.contactPhone')}</th>
                            <th>{t('personInvitations.columns.status')}</th>
                            <th>{t('personInvitations.columns.createdAt')}</th>
                            <th></th>
                          </tr>
                        </thead>
                        <tbody>
                          {invitations.map((inv) => (
                            <tr key={inv.id}>
                              <td>{inv.companyName || '-'}</td>
                              <td>{inv.contactEmail}</td>
                              <td>{inv.contactPhone || '-'}</td>
                              <td>
                                <span className={`badge ${getStatusBadgeClass(inv.status)}`}>
                                  {t(`personInvitations.status.${inv.status}`)}
                                </span>
                                <div className="small text-muted mt-1">
                                  {t(`personInvitations.emailStatus.${inv.emailStatus || 'not_sent'}`)}
                                </div>
                              </td>
                              <td>{inv.createdAt ? new Date(inv.createdAt).toLocaleDateString() : '-'}</td>
                              <td>
                                {['pending', 'sent'].includes(inv.status) && (
                                  <button
                                    className="btn btn-sm btn-outline-primary"
                                    disabled={resendingId === inv.id}
                                    onClick={() => handleResend(inv)}
                                  >
                                    <i className="feather-send me-1"></i>
                                    {resendingId === inv.id
                                      ? t('personInvitations.resending')
                                      : t('personInvitations.resend')}
                                  </button>
                                )}
                                {registrationUrls[inv.id] && (
                                  <button
                                    className="btn btn-sm btn-link"
                                    onClick={() => handleCopyLink(inv.id)}
                                  >
                                    {copiedId === inv.id
                                      ? t('personInvitations.linkCopied')
                                      : t('personInvitations.copyNewLink')}
                                  </button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div className="d-md-none">
                    {invitations.map((inv) => (
                      <div key={inv.id} className="card mb-2">
                        <div className="card-body py-2 px-3">
                          <div className="flex-grow-1" style={{ minWidth: 0 }}>
                            <div className="d-flex align-items-center gap-2 mb-1">
                              <span className="fw-semibold text-truncate">{inv.companyName || '-'}</span>
                              <span className={`badge ${getStatusBadgeClass(inv.status)} flex-shrink-0`}>
                                {t(`personInvitations.status.${inv.status}`)}
                              </span>
                            </div>
                            {inv.contactEmail && (
                              <div className="text-muted small text-truncate"><i className="feather-mail me-1"></i>{inv.contactEmail}</div>
                            )}
                            {inv.contactPhone && (
                              <div className="text-muted small"><i className="feather-phone me-1"></i>{inv.contactPhone}</div>
                            )}
                            <div className="text-muted small mt-1">
                              {t(`personInvitations.emailStatus.${inv.emailStatus || 'not_sent'}`)}
                            </div>
                            <div className="text-muted small mt-1"><i className="feather-calendar me-1"></i>{inv.createdAt ? new Date(inv.createdAt).toLocaleDateString() : '-'}</div>
                            {['pending', 'sent'].includes(inv.status) && (
                              <button className="btn btn-sm btn-outline-primary mt-2" disabled={resendingId === inv.id} onClick={() => handleResend(inv)}>
                                <i className="feather-send me-1"></i>
                                {resendingId === inv.id ? t('personInvitations.resending') : t('personInvitations.resend')}
                              </button>
                            )}
                            {registrationUrls[inv.id] && (
                              <button className="btn btn-sm btn-link mt-2" onClick={() => handleCopyLink(inv.id)}>
                                {copiedId === inv.id ? t('personInvitations.linkCopied') : t('personInvitations.copyNewLink')}
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </Card>
    </DashboardLayout>
  );
}
